import { useCallback, useEffect, useRef, useState } from 'react';

const COLLAPSED_HEIGHT = 70;

type Position = {
  x: number;
  y: number;
};

type Size = {
  width: number;
  height: number;
};

type UseDragResizeProps = {
  isCollapsed: boolean;
  wasCollapsed: boolean;
  setIsCollapsed: (isCollapsed: boolean | ((prev: boolean) => boolean)) => void;
  setWasCollapsed: (wasCollapsed: boolean) => void;
  position: Position;
  setPosition: (position: Position | ((prev: Position) => Position)) => void;
  size: Size;
  setSize: (size: Size) => void;
};

export const useDragResize = ({
  isCollapsed,
  wasCollapsed,
  setIsCollapsed,
  setWasCollapsed,
  position,
  setPosition,
  size,
  setSize,
}: UseDragResizeProps) => {
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const dragOffsetRef = useRef({ x: 0, y: 0 });
  const resizeOffsetRef = useRef({ x: 0, y: 0 });

  const onHeaderMouseDown = useCallback((e: React.MouseEvent) => {
    try {
      setIsDragging(true);
      const currentTarget = e.currentTarget;
      if (!(currentTarget instanceof HTMLElement)) return;
      const el = currentTarget.closest('[data-devpanel-root]');
      if (!(el instanceof HTMLElement)) return;
      const rect = el.getBoundingClientRect();
      dragOffsetRef.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };
    } catch (error) {
      console.warn('[DevModal] Error starting drag:', error);
      setIsDragging(false);
    }
  }, []);

  const onResizeMouseDown = useCallback((e: React.MouseEvent) => {
    try {
      e.preventDefault();
      setIsResizing(true);
      const currentTarget = e.currentTarget;
      if (!(currentTarget instanceof HTMLElement)) return;
      const el = currentTarget.closest('[data-devpanel-root]');
      if (!(el instanceof HTMLElement)) return;
      const rect = el.getBoundingClientRect();
      resizeOffsetRef.current = {
        x: e.clientX - (rect.left + rect.width),
        y: e.clientY - (rect.top + rect.height),
      };
    } catch (error) {
      console.warn('[DevModal] Error starting resize:', error);
      setIsResizing(false);
    }
  }, []);

  const toggleCollapse = useCallback(() => {
    setWasCollapsed(isCollapsed);
    setIsCollapsed((v: boolean) => !v);
  }, [isCollapsed, setIsCollapsed, setWasCollapsed]);

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (isDragging) {
        const newX = e.clientX - dragOffsetRef.current.x;
        const newY = e.clientY - dragOffsetRef.current.y;
        const currentHeight = isCollapsed ? COLLAPSED_HEIGHT : size.height;
        setPosition({
          x: Math.max(0, Math.min(newX, window.innerWidth - size.width)),
          y: Math.max(0, Math.min(newY, window.innerHeight - currentHeight)),
        });
      } else if (isResizing) {
        const w = Math.max(
          320,
          Math.min(e.clientX - position.x - resizeOffsetRef.current.x, window.innerWidth - position.x - 20),
        );
        const h = Math.max(
          300,
          Math.min(e.clientY - position.y - resizeOffsetRef.current.y, window.innerHeight - position.y - 20),
        );
        setSize({ width: w, height: h });
      }
    };
    const onUp = () => {
      setIsDragging(false);
      setIsResizing(false);
    };
    if (isDragging || isResizing) {
      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
      document.body.style.cursor = isDragging ? 'grabbing' : isResizing ? 'nw-resize' : '';
    }
    return () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.body.style.cursor = '';
    };
  }, [isDragging, isResizing, isCollapsed, size.height, size.width, position.x, position.y, setPosition, setSize]);

  useEffect(() => {
    if (!isCollapsed && wasCollapsed) {
      const viewportH = window.innerHeight;
      if (position.y + size.height > viewportH) {
        setPosition((p) => ({ ...p, y: Math.max(0, viewportH - size.height) }));
      }
    }
  }, [isCollapsed, wasCollapsed, size.height, position.y, setPosition]);

  return {
    isDragging,
    isResizing,
    onHeaderMouseDown,
    onResizeMouseDown,
    toggleCollapse,
  };
};
